import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { LanguageProvider } from "@/lib/LanguageContext";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Layout from "@/components/Layout";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Discovery from "@/pages/Discovery";
import Clips from "@/pages/Clips";
import ClipGenerator from "@/pages/ClipGenerator";
import ViralDetection from "@/pages/ViralDetection";
import SmartScheduler from "@/pages/SmartScheduler";
import Autopilot from "@/pages/Autopilot";
import Publishing from "@/pages/Publishing";
import Analytics from "@/pages/Analytics";
import UnifiedAnalytics from "@/pages/UnifiedAnalytics";
import Webhooks from "@/pages/Webhooks";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Affiliates from "@/pages/Affiliates";
import Settings from "@/pages/Settings";
import SecuritySettings from "@/pages/SecuritySettings";
import Reports from "@/pages/Reports";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ForgotPassword from "@/pages/ForgotPassword";
import SecureVault from "@/pages/SecureVault";
import AgentAPI from "@/pages/AgentAPI";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="discovery" element={<Discovery />} />
        <Route path="viral" element={<ViralDetection />} />
        <Route path="autopilot" element={<Autopilot />} />
        <Route path="scheduler" element={<SmartScheduler />} />
        <Route path="clips" element={<Clips />} />
        <Route path="clip-generator" element={<ClipGenerator />} />
        <Route path="publishing" element={<Publishing />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="unified-analytics" element={<UnifiedAnalytics />} />
        <Route path="webhooks" element={<Webhooks />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="affiliates" element={<Affiliates />} />
        <Route path="settings" element={<Settings />} />
        <Route path="security" element={<SecuritySettings />} />
        <Route path="vault" element={<SecureVault />} />
        <Route path="agent-api" element={<AgentAPI />} />
        <Route path="reports" element={<Reports />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <UpdateBanner />
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
